import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import type {
    GetProfessionalForHdeExtractionQuery,
    GetProfessionalForHdeExtractionQueryVariables,
} from 'generated/graphql';
import { GetProfessionalForHdeExtractionDocument } from 'generated/graphql';
import { extractProfessionalForHde } from '~/modules/hde/utils/extract-professional-for-hde';

export const HDE_PROFESSIONAL_EXTRACTION_API_ROUTE =
    '/remix-api/app/hde-professional-extraction';

const QUERY_SCHEMA = z.object({
    urlSegment: z.string().min(1),
});

/**
 * API endpoint to get Professional data for HDE registration extraction.
 * Returns CompanyType and ProjectLocation when extractable from the profile.
 * Use with useFetcher for lazy loading when user selects a business profile.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        urlSegment: url.searchParams.get('urlSegment'),
    });

    if (!result.success) {
        return routerData(
            { error: 'Invalid query parameters' },
            { status: 400 },
        );
    }

    const { urlSegment } = result.data;
    const link = `/professional/${urlSegment}`;

    try {
        const queryResult = await context.client.query<
            GetProfessionalForHdeExtractionQuery,
            GetProfessionalForHdeExtractionQueryVariables
        >({
            query: GetProfessionalForHdeExtractionDocument,
            variables: { urlSegment: link },
        });

        const professional = queryResult.data?.Directory?.Professional;

        if (!professional || professional.__typename !== 'Professional') {
            return routerData(
                { error: 'Professional not found', extraction: null },
                { status: 404 },
            );
        }

        const extraction = extractProfessionalForHde({
            PrimaryCategory: professional.PrimaryCategory,
            LibraryLocations: professional.LibraryLocations,
        });

        return { extraction };
    } catch (error) {
        context.logger.error(
            `Error in ${HDE_PROFESSIONAL_EXTRACTION_API_ROUTE}`,
            { error },
        );
        return routerData(
            { error: 'Failed to fetch professional', extraction: null },
            { status: 500 },
        );
    }
};
